// src/hooks/useWidgetSections.ts
import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { addWidgetSection, deleteWidgetSection } from '../redux/slices/siteContentSlice';
import { createDefaultWidgetData } from '../utils/widgetDefaults';
import { getWidgetIndexForOperation } from '../utils/widgetIndexUtils';

/**
 * Custom hook for managing widget sections (add/delete operations)
 * Provides clean API for adding and removing widgets with unique IDs
 */
export const useWidgetSections = () => {
  const dispatch = useAppDispatch();
  const { data: siteContent } = useAppSelector((state) => state?.siteContent);
  
  // Get widgets arrays from Redux state
  const originalWidgets = siteContent?.widgets ?? [];
  const allHomeWidgets = siteContent?.homeWidgets ?? [];
  
  // Filter out footer widgets for display
  const widgets = allHomeWidgets.filter((widget: any) => 
    !widget?.type?.toLowerCase().includes("footer")
  );

  /**
   * Maps a display index (filtered list) to the actual store index
   */
  const getOriginalIndex = useCallback((displayIndex: number) => {
    return getWidgetIndexForOperation(displayIndex, widgets, originalWidgets);
  }, [widgets, originalWidgets]);

  /**
   * Generates a unique widget ID using crypto.randomUUID()
   * Format: WidgetType_UniqueID (e.g., EssentialBanner_A1B2C3D4)
   */
  const generateWidgetId = useCallback((widgetType: string): string => {
    const uuid = crypto.randomUUID();
    const shortId = uuid.split('-')[0].toUpperCase();
    return `${widgetType}_${shortId}`;
  }, []);

  /**
   * Adds a new widget section with a unique ID
   * @param widgetType - Type of widget to add (e.g., "EssentialBanner")
   * @param afterDisplayIndex - Optional index to insert after (in display list)
   */
  const addSection = useCallback((
    widgetType: string,
    afterDisplayIndex?: number
  ) => {
    const widgetData = createDefaultWidgetData(widgetType);
    const uniqueId = generateWidgetId(widgetType);
    
    const newWidget = {
      type: uniqueId, // Unique type for easy findIndex (e.g., EssentialBanner_A1B2C3D4)
      id: uniqueId, // Same as type for consistency
      data: widgetData,
    };
    
    // Convert display index to store index if provided
    const widgetsAfterIndex = afterDisplayIndex !== undefined ? 
      getOriginalIndex(afterDisplayIndex) : undefined;
      
    dispatch(addWidgetSection({ widget: newWidget, afterIndex: widgetsAfterIndex }));
  }, [dispatch, generateWidgetId, getOriginalIndex]);

  /**
   * Deletes a widget section
   * Prevents deletion if it's the last non-footer widget
   * @param displayIndex - Index in the display list (filtered)
   */
  const deleteSection = useCallback((displayIndex: number) => {
    const widgetsIndex = getOriginalIndex(displayIndex);
    const nonFooterCount = allHomeWidgets.filter(
      (w: any) => !w.type.toLowerCase().includes('footer')
    ).length;
    
    // Prevent deleting the last non-footer widget
    if (nonFooterCount > 1) {
      dispatch(deleteWidgetSection(widgetsIndex));
    }
  }, [dispatch, allHomeWidgets, getOriginalIndex]);

  return {
    addSection,      // Function to add a widget section
    deleteSection,   // Function to delete a widget section
    widgets,         // Filtered widgets array (no footers)
    hasWidgets: widgets.length > 0, // Boolean indicating if widgets exist
  };
};
